/**
 * OaSIS Explorer Frontend - Layout Component
 * Task 30.10: Navbar Redesign
 * 
 * Main application layout with dark-mode-first design
 * Uses slim UsageStrip + MainNavbar pattern
 */

import React from 'react';
import { Outlet, Link } from 'react-router-dom';
import { useTranslation } from '../../contexts';
import { Navigation, UsageStrip } from '../navigation';
import { TrialBanner } from '../common';
import { useSearchUsage } from '../../hooks/useSearchUsage';

/**
 * Main application layout component
 * Layout order: UsageStrip (for non-Pro) -> Navigation -> Content -> Footer
 */
export const Layout: React.FC = () => {
  const t = useTranslation();
  const { isTrialing, trialDaysRemaining } = useSearchUsage();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
      {/* Skip to content link for accessibility */}
      <a
        href="#main-content"
        className="skip-link"
      >
        {t('a11y.skipToContent')}
      </a>

      {/* Usage Strip (Task 30.10) - slim indicator for guests/free users */}
      <UsageStrip />

      {/* Trial Banner (Task 29.7) - shows when trial is ending soon */}
      {isTrialing && trialDaysRemaining !== null && (
        <TrialBanner daysRemaining={trialDaysRemaining} />
      )}

      {/* Navigation */}
      <Navigation />

      {/* Main content area */}
      <main
        id="main-content"
        className="flex-1"
        role="main"
      >
        <Outlet />
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
};

/**
 * Footer component
 * Per STYLE_GUIDE.md: bg-slate-900 with border-slate-800
 */
const Footer: React.FC = () => {
  const t = useTranslation();

  return (
    <footer className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 mt-auto">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-6">
          {/* About Section */}
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <div className="w-6 h-6 bg-primary-600 rounded-md flex items-center justify-center">
                <span className="text-white font-bold text-xs">O</span>
              </div>
              <span className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('app.title')}
              </span>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {t('footer.about.description')}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-semibold text-lg mb-4 text-gray-900 dark:text-white">
              {t('footer.links.title')}
            </h3>
            <nav className="flex flex-col gap-2">
              <Link 
                to="/about" 
                className="flex text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
              >
                {t('nav.about')}
              </Link>
              <Link 
                to="/about#datasets" 
                className="flex text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
              >
                {t('nav.datasets')}
              </Link>
              <Link 
                to="/privacy" 
                className="flex text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
              >
                {t('nav.privacy')}
              </Link>
              <Link 
                to="/terms" 
                className="flex text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
              >
                {t('nav.terms')}
              </Link>
              <Link 
                to="/contact" 
                className="flex text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
              >
                {t('nav.contact')}
              </Link>
            </nav>
          </div>

          {/* Contact Information */}
          <div>
            <h3 className="font-semibold text-lg mb-4 text-gray-900 dark:text-white">
              {t('footer.contact.title')}
            </h3>
            <div className="flex flex-col gap-3">
              <a
                href="mailto:support@oasis-explorer.com"
                className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span>support@oasis-explorer.com</span>
              </a>
              <p className="text-xs text-gray-500 dark:text-gray-500">
                {t('footer.contact.supportHours')}
              </p>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-200 dark:border-gray-700 pt-6 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            © {new Date().getFullYear()} OaSIS Explorer. {t('footer.rights')}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-500">
            {t('footer.dataSource')}
          </p>
        </div>
      </div>
    </footer>
  );
};

/**
 * Container component for consistent page spacing
 */
export const Container: React.FC<{
  children: React.ReactNode;
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
}> = ({
  children,
  className = '',
  size = 'xl',
}) => {
  const sizeClasses = {
    sm: 'max-w-2xl',
    md: 'max-w-4xl',
    lg: 'max-w-6xl',
    xl: 'max-w-7xl',
    full: 'max-w-full',
  };

  return (
    <div className={`${sizeClasses[size]} mx-auto px-4 sm:px-6 lg:px-8 ${className}`}>
      {children}
    </div>
  );
};

/**
 * Page header component
 */
export const PageHeader: React.FC<{
  title: string;
  subtitle?: string;
  children?: React.ReactNode;
  className?: string;
}> = ({
  title,
  subtitle,
  children,
  className = '',
}) => {
  return (
    <div className={`bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 ${className}`}>
      <Container>
        <div className="pt-8 pb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div className="mb-4 sm:mb-0">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">
                {title}
              </h1>
              {subtitle && (
                <p className="text-gray-600 dark:text-gray-400">
                  {subtitle}
                </p>
              )}
            </div>
            {children && (
              <div className="flex-shrink-0">
                {children}
              </div>
            )}
          </div>
        </div>
      </Container>
    </div>
  );
};

/**
 * Page content wrapper
 */
export const PageContent: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className = '' }) => {
  return (
    <div className={`py-8 ${className}`}>
      <Container>
        {children}
      </Container>
    </div>
  );
};
